import { memo, useMemo } from 'react';

import Colors from '@dynatrace/strato-design-tokens/colors';
import {
  BandChartIcon,
  BarChartIcon,
  LineChartIcon,
  StackedAreaChartIcon,
} from '@dynatrace/strato-icons';

import { useChartLayoutOrientation } from '../../../core/components/chart-layout/hooks/useChartLayoutOrientation.js';
import { getLegendOrientation } from '../../../core/components/chart-layout/utils/orientation.js';
import { reduceExploreCancelTooltip } from '../../../core/components/interactions-state-helpers/reducers/explore-reducers.js';
import { ChartLegend } from '../../../core/components/legend/categorical/ChartLegend.js';
import { useLegendSeriesActions } from '../../../core/components/legend/categorical/hooks/useLegendSeriesActions.js';
import {
  ChartIcon,
  LegendItem,
} from '../../../core/components/legend/categorical/types/legend.js';
import { safeGet } from '../../../core/utils/safe-get.js';
import { singleLineName } from '../../../core/utils/single-line-name.js';
import { useSetState } from '../../hooks/store/selectors.js';
import { useTimeseriesData } from '../../hooks/useTimeseriesData.js';
import { useTruncationMode } from '../../hooks/useTruncationMode.js';
import {
  SeriesMetadata,
  TimeseriesPerVariant,
  TimeseriesSlotProps,
} from '../../types/timeseries.js';
import { hasMultiGeometries } from '../../utils/timeseries.js';

export const renderIcon = (variant?: ChartIcon) => {
  switch (variant) {
    case 'bar':
      return (
        <BarChartIcon
          data-testid="chart-legend-item-bar-icon-visible"
          size="small"
        />
      );
    case 'area':
      return (
        <StackedAreaChartIcon
          data-testid="chart-legend-item-area-icon-visible"
          size="small"
        />
      );
    case 'band':
      return (
        <BandChartIcon
          data-testid="chart-legend-item-band-icon-visible"
          size="small"
        />
      );
    case 'line':
    default:
      return (
        <LineChartIcon
          data-testid="chart-legend-item-line-icon-visible"
          size="small"
        />
      );
  }
};

const createItemData = (
  series: TimeseriesPerVariant,
  seriesMap: WeakMap<object, SeriesMetadata>,
): LegendItem[] => {
  const isMultiGeometries = hasMultiGeometries(series);
  const result: LegendItem[] = [];

  for (const variant of series.shapePriority) {
    const variantSeries = series[variant];

    for (let index = 0; index < variantSeries.length; index++) {
      const singleSeries = variantSeries[index];
      const seriesMetadata = safeGet(seriesMap, singleSeries);

      result.push({
        id: seriesMetadata.id ?? `item-${index}`,
        color: seriesMetadata.color ?? Colors.Text.Neutral.Default,
        icon: isMultiGeometries ? renderIcon(variant) : undefined,
        name: singleLineName(singleSeries.name) ?? seriesMetadata.id,
      });
    }
  }

  return result;
};

/**
 * ChartLegend component.
 */
export const TimeseriesChartLegend = memo(() => {
  const { dataPerVariant, data, seriesMap } = useTimeseriesData();
  const truncationMode = useTruncationMode();
  const setState = useSetState();
  const { orientation: layoutOrientation } = useChartLayoutOrientation();
  const legendOrientation = getLegendOrientation(layoutOrientation);

  const legendActions = useLegendSeriesActions<TimeseriesSlotProps>(
    data,
    (s) => safeGet(seriesMap, s).id,
  );
  const legendItems = useMemo(
    () => createItemData(dataPerVariant, seriesMap),
    [dataPerVariant, seriesMap],
  );

  const updateHighlightedSeries = (items?: LegendItem[] | undefined) => {
    const hoveredItemsIds = items?.map((item) => item.id);

    const highlightedSeriesIds = hoveredItemsIds?.length
      ? hoveredItemsIds
      : undefined;

    setState((prev) => ({
      ...prev,
      highlightedSeriesIds,
    }));
  };

  const updateVisibleSeries = (visibleSeries: LegendItem[]) => {
    const visibleSeriesSet = new Set(visibleSeries.map((s) => s.id));

    setState((prev) =>
      reduceExploreCancelTooltip({
        ...prev,
        filteredSeriesIds: visibleSeriesSet,
      }),
    );
  };

  return (
    <ChartLegend
      items={legendItems}
      orientation={legendOrientation}
      interactionsDisabled={false}
      onHover={updateHighlightedSeries}
      onClick={updateVisibleSeries}
      onSeriesAction={legendActions}
      truncationMode={truncationMode}
    />
  );
});

TimeseriesChartLegend['displayName'] = 'TimeseriesChartLegend';
